import { QueryClient, useMutation, useQueryClient } from "@tanstack/vue-query";
import axios from "axios";
import {
  type Category,
  CategoryType,
  MePreferenceCategoriesApi,
} from "mws-ts-v2-sdk";

import type { CategoryFollowedSource } from "~/analytics/segment";
import { queries } from "~/queries";

import { preferencesQueryKeys } from "./preferences";

const getCategoryPreferencesApi = () => {
  return new MePreferenceCategoriesApi(
    undefined,
    useRuntimeConfig().public.catalogServiceUrl,
    axios,
  );
};

const invalidateCategoryPreferences = (
  queryClient: QueryClient,
  updatedCategorySlug?: string,
) => {
  queryClient.invalidateQueries({
    queryKey: preferencesQueryKeys.list._def,
  });

  queryClient.invalidateQueries({
    queryKey: queries.followedCategories.list._def,
  });

  if (updatedCategorySlug) {
    queryClient.invalidateQueries({
      queryKey: queries.categories.details(updatedCategorySlug).queryKey,
    });
  }
};

const getRelatedSportsCategoryName = async (
  {
    categorySlug,
    categoryName,
    categoryType,
  }: {
    categorySlug: string;
    categoryName: string;
    categoryType?: CategoryType;
  },
  queryClient: QueryClient,
) => {
  if (categoryType === CategoryType.Sport) return categoryName;

  let sportName;
  try {
    const relatedCategories = await queryClient.fetchQuery({
      ...queries.categories.relatedCategories(categorySlug),
    });

    sportName = relatedCategories?.find(
      (c) => c.externalReference?.type === CategoryType.Sport,
    )?.name;
  } catch {
    sportName = undefined;
  }

  return sportName;
};

export const useFollowCategoriesMutation = () => {
  const queryClient = useQueryClient();
  const { categoryFollowed, categoryUnfollowed } = useAnalytics();

  const mutationFn = async (request: {
    followCategories: (Partial<Category> & { id: string })[];
    unfollowCategories: (Partial<Category> & { id: string })[];
  }) => {
    const response =
      await getCategoryPreferencesApi().v2MePreferencesCategoriesPatch({
        v2MePreferencesCategoriesPatchRequest: {
          followCategoryIds: request.followCategories.map((c) => c.id),
          unfollowCategoryIds: request.unfollowCategories.map((c) => c.id),
        },
      });

    return response.data;
  };

  return useMutation({
    mutationFn,
    onSuccess: (_, variables) => {
      variables.followCategories.map(async (c) => {
        const sportName = await getRelatedSportsCategoryName(
          {
            categorySlug: c.slug ?? "",
            categoryName: c.name ?? "",
            categoryType: c.externalReference?.type,
          },
          queryClient,
        );

        categoryFollowed({
          category_id: c.id,
          category_name: c.name ?? "",
          source: "onboarding",
          sport_category: sportName,
        });
      });

      variables.unfollowCategories.forEach((c) => {
        categoryUnfollowed({
          category_id: c.id,
          category_name: c.name ?? "",
          source: "onboarding",
        });
      });

      invalidateCategoryPreferences(queryClient);
    },
  });
};

type TrackData = {
  followType: CategoryFollowedSource;
};

export const useFollowCategoryMutation = () => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useI18n();
  const { categoryFollowed, categoryUnfollowed } = useAnalytics();

  const mutationFn = async (request: {
    id: string;
    newFollowStatus: boolean;
    trackData: TrackData;
  }) => {
    const response =
      await getCategoryPreferencesApi().v2MePreferencesCategoriesCategoryIdPut({
        categoryId: request.id,
        v2MePreferencesCategoriesCategoryIdPutRequest: {
          follow: request.newFollowStatus,
        },
      });

    return response.data;
  };

  return useMutation({
    mutationFn,
    onError: () => {
      toast.clear();
      toast.error(t("customerPreferences.followFeedback.error"));
    },
    onSuccess: async (_, variables) => {
      const category = await queryClient.ensureQueryData(
        queries.categories.details(variables.id),
      );

      const sportName = await getRelatedSportsCategoryName(
        {
          categorySlug: category.slug,
          categoryName: category.name,
          categoryType: category.externalReference?.type,
        },
        queryClient,
      );

      const trackParams = {
        category_id: variables.id,
        category_name: category.name,
        source: variables.trackData.followType,
        sport_category: sportName,
      };

      if (variables.newFollowStatus) categoryFollowed(trackParams);
      else categoryUnfollowed(trackParams);

      invalidateCategoryPreferences(queryClient, category.slug);
    },
  });
};
